import React from "react";
import {
    Box, Grid, TextField, Checkbox, FormControlLabel, Typography,
    Button, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { bottleOrderStatusList } from "../common/statusLists";
import { fieldSx, SAMPLE_MATRIX_OPTIONS } from "./bottleOrderFormHelpers";

// Layout (ENB-589): BO info on the top line, then person / date / action button
// lined up per column, left to right: Order Reviewed, Kit Built, Kit Reviewed.
const ReviewPrepStep = ({ formData, handleChange, handleCheckboxChange, loading, openConfirm }) => (
    <>
        {/* Row 1 — status, description, matrix, created by */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl variant="outlined" fullWidth size="small" sx={fieldSx}>
                    <InputLabel>Bottle Order Status</InputLabel>
                    <Select
                        value={formData.shippingStatus?.trim() || ""}
                        label="Bottle Order Status"
                        onChange={handleChange("shippingStatus")}
                    >
                        {bottleOrderStatusList.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Description"
                    value={formData.description} onChange={handleChange("description")}
                    variant="outlined" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl variant="outlined" fullWidth size="small" sx={fieldSx}>
                    <InputLabel>Sample Matrix</InputLabel>
                    <Select
                        value={(formData.sampleMatrix || "").trim()}
                        label="Sample Matrix"
                        onChange={handleChange("sampleMatrix")}
                    >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {SAMPLE_MATRIX_OPTIONS.map((v) => (
                            <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Created By"
                    value={formData.createdBy} disabled variant="outlined" sx={fieldSx} />
            </Grid>
        </Grid>

        {/* Row 2 — order number + reviewers */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Order Number" type="number"
                    value={formData.orderNumber} disabled variant="outlined" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Reviewed By"
                    value={formData.orderReviewedBy} onChange={handleChange("orderReviewedBy")}
                    variant="outlined" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Kit Built By"
                    value={formData.preparedBy} onChange={handleChange("preparedBy")}
                    variant="outlined" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Kit Reviewed By"
                    value={formData.reviewedBy} onChange={handleChange("reviewedBy")}
                    variant="outlined" sx={fieldSx} />
            </Grid>
        </Grid>

        {/* Row 3 — quote number + review dates */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Quote Number"
                    value={formData.quoteNumber} onChange={handleChange("quoteNumber")}
                    variant="outlined" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Order Reviewed On" type="date"
                    value={formData.orderReviewedByDate} onChange={handleChange("orderReviewedByDate")}
                    variant="outlined" InputLabelProps={{ shrink: true }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Kit Built On" type="date"
                    value={formData.preparedByDate} onChange={handleChange("preparedByDate")}
                    variant="outlined" InputLabelProps={{ shrink: true }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Kit Reviewed On" type="date"
                    value={formData.reviewedByDate} onChange={handleChange("reviewedByDate")}
                    variant="outlined" InputLabelProps={{ shrink: true }} sx={fieldSx} />
            </Grid>
        </Grid>

        {/* Row 4 — due dates */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                        fullWidth size="small" label="Shipping Due Date" type="date"
                        value={formData.dueDate} onChange={handleChange("dueDate")}
                        variant="outlined" InputLabelProps={{ shrink: true }} sx={fieldSx}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.shippingDueDateFirm}
                                onChange={handleCheckboxChange("shippingDueDateFirm")}
                                color="primary" size="small"
                            />
                        }
                        label={<Typography sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>Firm</Typography>}
                        sx={{ mr: 0, ml: 0 }}
                    />
                </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Kit Build Due Date" type="date"
                    value={formData.kitPrepDueDate} onChange={handleChange("kitPrepDueDate")}
                    variant="outlined" InputLabelProps={{ shrink: true }} sx={fieldSx} />
            </Grid>
        </Grid>

        {/* Row 5 — send to IDEM + one action button under each reviewer column */}
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.sendToIdem}
                            onChange={handleCheckboxChange("sendToIdem")}
                            color="primary" size="small"
                        />
                    }
                    label={<Typography sx={{ fontSize: "0.875rem" }}>Send to IDEM</Typography>}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="contained" color="primary" startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => openConfirm("markAsReviewed")} disabled={loading} size="small">
                    Mark as Reviewed
                </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="contained" color="success" startIcon={<BuildOutlinedIcon />}
                    onClick={() => openConfirm("markAsBuilt")} disabled={loading} size="small">
                    Mark as Built
                </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="contained" color="warning" startIcon={<VerifiedOutlinedIcon />}
                    onClick={() => openConfirm("markKitReviewed")} disabled={loading} size="small">
                    Mark Kit as Reviewed
                </Button>
            </Grid>
            <Grid item xs={12} md={9} sx={{ ml: { md: "25%" } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Each button will confirm before updating the status and recording your initials.
                </Typography>
            </Grid>
        </Grid>
    </>
);

export default ReviewPrepStep;
